<script setup>
import { computed, onMounted } from 'vue';
import { useRoute, useRouter, RouterLink } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePolicy } from 'dashboard/composables/usePolicy';
import {
  getPermissionTokensForAccount,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper';
import { buildRealEstateNavigationSections } from 'dashboard/composables/lais/useRealEstateNavigation';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import { buildNativeChatwootNavigation } from './nativeChatwootNavigation';
import {
  LAIS_SIDEBAR_ACTIVE_ICON_CLASS,
  LAIS_SIDEBAR_ACTIVE_LINK_CLASS,
  LAIS_SIDEBAR_BADGE_CLASS,
  LAIS_SIDEBAR_CLASS,
  LAIS_SIDEBAR_FOOTER_CLASS,
  LAIS_SIDEBAR_HEADER_CLASS,
  LAIS_SIDEBAR_INACTIVE_ICON_CLASS,
  LAIS_SIDEBAR_INACTIVE_LINK_CLASS,
  LAIS_SIDEBAR_LINK_CLASS,
  LAIS_SIDEBAR_SECTION_TITLE_CLASS,
} from 'dashboard/helper/laisClassNames';

const props = defineProps({
  isMobileSidebarOpen: { type: Boolean, default: false },
});

const emit = defineEmits(['closeMobileSidebar', 'openKeyShortcutModal']);

const route = useRoute();
const router = useRouter();
const store = useStore();
const account = useAccount();
const { shouldShow } = usePolicy();
const { t } = useI18n();

const safeRoute = (name, params, query) => {
  try {
    return account.accountScopedRoute(name, params, query);
  } catch {
    return { path: '/' };
  }
};

const globalConfig = computed(() => store.getters['globalConfig/get'] || {});
const currentAccount = computed(() => store.getters.getCurrentAccount || {});
const currentPermissions = computed(() =>
  getPermissionTokensForAccount(currentAccount.value)
);
const installationInitial = computed(() =>
  (globalConfig.value.installationName || 'Lais').slice(0, 1).toUpperCase()
);
const inboxes = computed(() =>
  (store.getters['inboxes/getInboxes'] || [])
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
);
const labels = computed(() => store.getters['labels/getLabelsOnSidebar'] || []);
const teams = computed(() => store.getters['teams/getMyTeams'] || []);
const contactCustomViews = computed(
  () => store.getters['customViews/getContactCustomViews'] || []
);
const conversationCustomViews = computed(
  () => store.getters['customViews/getConversationCustomViews'] || []
);
const portals = computed(() => store.getters['portals/allPortals'] || []);

const imudeiParityRouteNames = new Set([
  'operacao_imobiliaria_vendas',
  'operacao_imobiliaria_vendas_detalhes',
  'operacao_imobiliaria_vendas_revisar',
  'operacao_imobiliaria_vendas_cadastro',
  'operacao_imobiliaria_vendas_nova',
  'lais_tenants_rent_flows',
  'lais_tenants_rent_flows_status',
  'lais_tenants_rent_flows_status_phase',
  'visitas_index',
  'visita_show',
  'visita_edit',
  'visita_new',
  'operacao_imobiliaria_pos_venda',
  'operacao_imobiliaria_pos_venda_detalhes',
  'operacao_imobiliaria_pos_venda_status',
  'operacao_imobiliaria_financeiro',
  'operacao_imobiliaria_financeiro_detalhes',
  'operacao_imobiliaria_financeiro_status',
  'operacao_imobiliaria_financeiro_status_alterar',
  'operacao_imobiliaria_corretor',
  'financeiro_index',
  'financeiro_comissoes_index',
  'operacao_imobiliaria_vendas_comissao',
]);

const isImudeiParityRoute = computed(() =>
  imudeiParityRouteNames.has(route.name)
);
const isProposalCompactSidebar = computed(() =>
  [
    'lais_tenants_rent_flows',
    'lais_tenants_rent_flows_status',
    'lais_tenants_rent_flows_status_phase',
  ].includes(route.name)
);

onMounted(() => {
  store.dispatch('notifications/unReadCount');
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
  store.dispatch('portals/index');
});

const isActive = item => {
  const names = [item.routeName, ...(item.activeOn || [])].filter(Boolean);
  return names.includes(route.name);
};

const visibleForRole = item => {
  const requiredPermissions = item.permissions || item.roles || [];
  if (
    requiredPermissions.length &&
    !hasPermissions(requiredPermissions, currentPermissions.value)
  ) {
    return false;
  }

  if (!item.nativeRoute) return true;

  const policyRouteName = item.policyRouteName || item.routeName;
  const routeRecord = router
    .getRoutes()
    .find(candidate => candidate.name === policyRouteName);

  if (!routeRecord) return false;

  const { featureFlag, permissions, installationTypes } = routeRecord.meta;
  return shouldShow(featureFlag, permissions, installationTypes);
};

const imudeiParitySections = computed(() => [
  {
    items: [
      {
        key: 'imudei-dashboard',
        label: 'Dashboard',
        icon: 'i-lucide-layout-dashboard',
        routeName: 'inicio_index',
      },
      {
        key: 'imudei-properties',
        label: 'Imóveis',
        icon: 'i-lucide-building-2',
        routeName: 'imoveis_index',
      },
      {
        key: 'imudei-leads',
        label: 'Leads',
        icon: 'i-lucide-users-round',
        routeName: 'lais_leads_index',
      },
      {
        key: 'imudei-clients',
        label: 'Clientes',
        icon: 'i-lucide-contact-round',
        routeName: 'contacts_dashboard_index',
      },
      {
        key: 'imudei-service',
        label: 'Atendimentos',
        icon: 'i-lucide-message-circle',
        routeName: 'home',
      },
      {
        key: 'imudei-proposals',
        label: 'Propostas',
        icon: 'i-lucide-file-text',
        routeName: 'lais_tenants_rent_flows',
        activeOn: [
          'lais_tenants_rent_flows_status',
          'lais_tenants_rent_flows_status_phase',
        ],
      },
      {
        key: 'imudei-sales',
        label: 'Vendas',
        icon: 'i-lucide-tags',
        routeName: 'operacao_imobiliaria_vendas',
        activeOn: [
          'operacao_imobiliaria_vendas_detalhes',
          'operacao_imobiliaria_vendas_revisar',
          'operacao_imobiliaria_vendas_cadastro',
          'operacao_imobiliaria_vendas_nova',
        ],
      },
      {
        key: 'imudei-finance',
        label: 'Financeiro',
        icon: 'i-lucide-circle-dollar-sign',
        routeName: 'operacao_imobiliaria_financeiro',
        activeOn: [
          'operacao_imobiliaria_financeiro_detalhes',
          'operacao_imobiliaria_financeiro_status',
          'operacao_imobiliaria_financeiro_status_alterar',
        ],
      },
      {
        key: 'imudei-commissions',
        label: 'Comissões',
        icon: 'i-lucide-chart-no-axes-combined',
        routeName: 'financeiro_index',
        query: { tab: 'comissoes' },
        activeOn: [
          'financeiro_comissoes_index',
          'operacao_imobiliaria_vendas_comissao',
        ],
      },
      {
        key: 'imudei-reports',
        label: 'Relatórios',
        icon: 'i-lucide-chart-pie',
        routeName: 'relatorio_index',
      },
      {
        key: 'imudei-settings',
        label: 'Configurações',
        icon: 'i-lucide-settings',
        routeName: 'general_settings_index',
      },
    ].map(item => ({
      ...item,
      to: safeRoute(item.routeName, item.params, item.query),
    })),
  },
]);

const sections = computed(() => {
  if (isImudeiParityRoute.value) return imudeiParitySections.value;

  const realEstateSections = buildRealEstateNavigationSections({
    t,
    routeFor: safeRoute,
    account: currentAccount.value,
    permissions: currentPermissions.value,
  });

  const nativeSections = buildNativeChatwootNavigation({
    t,
    inboxes: inboxes.value,
    labels: labels.value,
    teams: teams.value,
    contactCustomViews: contactCustomViews.value,
    conversationCustomViews: conversationCustomViews.value,
    portals: portals.value,
  }).map(section => ({
    ...section,
    items: section.items.map(item => ({
      ...item,
      to: safeRoute(item.routeName, item.params, item.query),
    })),
  }));

  return [...realEstateSections, ...nativeSections]
    .map(section => ({
      ...section,
      items: section.items.filter(visibleForRole),
    }))
    .filter(section => section.items.length > 0);
});

const closeMobileSidebar = () => emit('closeMobileSidebar');
const closeAfterNavigation = () => {
  if (props.isMobileSidebarOpen) {
    closeMobileSidebar();
  }
};
const openKeyShortcutModal = () => {
  emit('openKeyShortcutModal');
  closeAfterNavigation();
};
</script>

<template>
  <transition
    enter-active-class="transition-opacity duration-200"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity duration-200"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="props.isMobileSidebarOpen"
      class="fixed inset-0 z-30 bg-black/40 lg:hidden"
      @click="closeMobileSidebar"
    />
  </transition>

  <aside
    :class="[
      LAIS_SIDEBAR_CLASS,
      isProposalCompactSidebar ? 'lg:w-[80px]' : '',
      props.isMobileSidebarOpen
        ? 'translate-x-0 shadow-lais-pop'
        : '-translate-x-full lg:shadow-none',
    ]"
    @click.self="closeMobileSidebar"
  >
    <header
      class="lais-sidebar__header"
      :class="[
        LAIS_SIDEBAR_HEADER_CLASS,
        isProposalCompactSidebar ? 'lg:justify-center lg:px-2' : '',
      ]"
    >
      <img
        :src="globalConfig.logo || '/brand-assets/logo.svg'"
        :alt="globalConfig.installationName || 'Lais'"
        class="lais-sidebar__logo h-7 w-auto"
        :class="isProposalCompactSidebar ? 'lg:hidden' : ''"
      />
      <span
        class="lais-sidebar__compact-logo"
        :class="isProposalCompactSidebar ? 'lg:inline' : ''"
        aria-hidden="true"
      >
        {{ installationInitial }}
      </span>
    </header>

    <nav class="lais-sidebar__nav flex-1 overflow-y-auto py-3 no-scrollbar">
      <template v-for="(section, sIdx) in sections" :key="sIdx">
        <div
          v-if="section.title"
          class="lais-sidebar__section-title-wrap mt-5 mb-1.5 px-6"
          :class="isProposalCompactSidebar ? 'lg:hidden' : ''"
        >
          <span
            class="lais-sidebar__section-title"
            :class="LAIS_SIDEBAR_SECTION_TITLE_CLASS"
          >
            {{ section.title }}
          </span>
        </div>

        <ul class="flex flex-col">
          <li v-for="item in section.items" :key="item.key || item.label">
            <RouterLink
              v-if="item.to && !item.href && !item.disabled"
              :to="item.to"
              class="lais-sidebar__link"
              :class="[
                LAIS_SIDEBAR_LINK_CLASS,
                isProposalCompactSidebar ? 'lg:justify-center lg:px-2' : '',
                isActive(item)
                  ? LAIS_SIDEBAR_ACTIVE_LINK_CLASS
                  : LAIS_SIDEBAR_INACTIVE_LINK_CLASS,
              ]"
              :aria-label="item.label"
              :title="item.label"
              @click="closeAfterNavigation"
            >
              <span
                class="shrink-0 size-5 transition-colors"
                :class="[
                  item.icon,
                  isActive(item)
                    ? LAIS_SIDEBAR_ACTIVE_ICON_CLASS
                    : LAIS_SIDEBAR_INACTIVE_ICON_CLASS,
                ]"
                aria-hidden="true"
              />
              <span
                class="lais-sidebar__link-label truncate flex-1"
                :class="isProposalCompactSidebar ? 'lg:hidden' : ''"
              >
                {{ item.label }}
              </span>
              <span
                v-if="item.badge"
                class="lais-sidebar__badge"
                :class="LAIS_SIDEBAR_BADGE_CLASS"
              >
                {{ item.badge }}
              </span>
            </RouterLink>

            <span
              v-else-if="item.to && !item.href && item.disabled"
              class="lais-sidebar__link cursor-not-allowed opacity-60"
              :class="[
                LAIS_SIDEBAR_LINK_CLASS,
                isProposalCompactSidebar ? 'lg:justify-center lg:px-2' : '',
                LAIS_SIDEBAR_INACTIVE_LINK_CLASS,
              ]"
              role="link"
              aria-disabled="true"
              :aria-label="item.disabledReasonLabel || item.label"
              :title="item.disabledReasonLabel || item.label"
            >
              <span
                class="shrink-0 size-5 transition-colors"
                :class="[item.icon, LAIS_SIDEBAR_INACTIVE_ICON_CLASS]"
                aria-hidden="true"
              />
              <span
                class="lais-sidebar__link-label truncate flex-1"
                :class="isProposalCompactSidebar ? 'lg:hidden' : ''"
              >
                {{ item.label }}
              </span>
              <span
                v-if="item.badge"
                class="lais-sidebar__badge"
                :class="LAIS_SIDEBAR_BADGE_CLASS"
              >
                {{ item.badge }}
              </span>
            </span>

            <a
              v-else-if="item.href"
              :href="item.href"
              target="_blank"
              rel="noopener noreferrer"
              class="lais-sidebar__link flex h-11 items-center gap-3 pl-6 pr-4 text-[14px] text-lais-text transition-colors hover:bg-lais-primary-50/30"
              :class="
                isProposalCompactSidebar ? 'lg:justify-center lg:px-2' : ''
              "
              :aria-label="item.label"
              :title="item.label"
              @click="closeAfterNavigation"
            >
              <span
                class="shrink-0 size-5 text-lais-icon"
                :class="item.icon"
                aria-hidden="true"
              />
              <span
                class="lais-sidebar__link-label truncate flex-1"
                :class="isProposalCompactSidebar ? 'lg:hidden' : ''"
              >
                {{ item.label }}
              </span>
            </a>
          </li>
        </ul>
      </template>
    </nav>

    <footer
      class="lais-sidebar__footer"
      :class="[
        LAIS_SIDEBAR_FOOTER_CLASS,
        isProposalCompactSidebar ? 'lg:px-2' : '',
      ]"
    >
      <div class="lais-sidebar__profile-menu w-full min-w-0">
        <SidebarProfileMenu
          :is-collapsed="isProposalCompactSidebar"
          @close="closeAfterNavigation"
          @open-key-shortcut-modal="openKeyShortcutModal"
        />
      </div>
    </footer>
  </aside>
</template>

<style scoped>
.lais-sidebar__compact-logo {
  display: none;
}

.lais-sidebar__profile-menu :deep(.relative.min-w-0) {
  width: 100%;
}

.lais-sidebar__profile-menu :deep(button) {
  min-height: 44px;
  width: 100%;
  border-radius: 14px;
  color: rgb(29, 21, 48);
  transition:
    background-color 160ms ease,
    color 160ms ease;
}

.lais-sidebar__profile-menu :deep(button:hover),
.lais-sidebar__profile-menu :deep(button:focus-visible) {
  background: rgb(245, 243, 255);
  outline: none;
}

.lais-sidebar__profile-menu :deep(.n-dropdown-body) {
  border-color: rgb(229, 222, 243);
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 18px 45px rgba(46, 31, 77, 0.16);
}

@media (min-width: 1024px) and (hover: hover) {
  .lais-sidebar--hover-collapse {
    --lais-sidebar-collapsed-width: 80px;
    --lais-sidebar-expanded-width: 240px;

    width: var(--lais-sidebar-collapsed-width);
    background: linear-gradient(180deg, #ffffff 0%, #fbfafc 100%);
    overflow-x: hidden;
    transition-duration: 200ms;
    transition-property: width, transform, box-shadow;
    transition-timing-function: ease-out;
  }

  .lais-sidebar--hover-collapse:hover {
    width: var(--lais-sidebar-expanded-width);
    box-shadow: 0 18px 45px rgba(46, 31, 77, 0.13);
  }

  .lais-sidebar__header,
  .lais-sidebar__footer,
  .lais-sidebar__section-title-wrap {
    padding-left: 0;
    padding-right: 0;
  }

  .lais-sidebar__header,
  .lais-sidebar__footer {
    width: var(--lais-sidebar-collapsed-width);
  }

  .lais-sidebar__header,
  .lais-sidebar__footer,
  .lais-sidebar__link {
    justify-content: center;
  }

  .lais-sidebar__header {
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
  }

  .lais-sidebar--hover-collapse:hover .lais-sidebar__header,
  .lais-sidebar--hover-collapse:hover .lais-sidebar__footer {
    width: var(--lais-sidebar-expanded-width);
    justify-content: flex-start;
    padding-left: 1.5rem;
    padding-right: 1rem;
  }

  .lais-sidebar--hover-collapse:hover .lais-sidebar__footer {
    padding-left: 1rem;
  }

  .lais-sidebar__profile-menu :deep(button) {
    justify-content: center;
    gap: 0;
    padding-left: 0.375rem;
    padding-right: 0.375rem;
  }

  .lais-sidebar__profile-menu :deep(button > div.min-w-0) {
    max-width: 0;
    opacity: 0;
    overflow: hidden;
    pointer-events: none;
    transform: translateX(-6px);
    transition-duration: 160ms;
    transition-property: opacity, max-width, transform;
    transition-timing-function: ease-out;
    white-space: nowrap;
  }

  .lais-sidebar--hover-collapse:hover
    .lais-sidebar__profile-menu
    :deep(button) {
    justify-content: flex-start;
    gap: 0.5rem;
  }

  .lais-sidebar--hover-collapse:hover
    .lais-sidebar__profile-menu
    :deep(button > div.min-w-0) {
    max-width: 168px;
    opacity: 1;
    pointer-events: auto;
    transform: translateX(0);
  }

  .lais-sidebar__nav {
    overflow-x: hidden;
    padding-top: 0.625rem;
    padding-bottom: 0.625rem;
  }

  .lais-sidebar__link {
    width: 48px;
    height: 44px;
    gap: 0;
    margin: 0.125rem auto;
    border-radius: 14px;
    padding-left: 0;
    padding-right: 0;
  }

  .lais-sidebar__link:hover {
    background: rgb(245, 243, 255);
  }

  .lais-sidebar__link > span:first-child {
    width: 1.25rem;
    height: 1.25rem;
  }

  .lais-sidebar--hover-collapse:hover .lais-sidebar__link {
    width: auto;
    gap: 0.75rem;
    margin-left: 0.5rem;
    margin-right: 0.5rem;
    justify-content: flex-start;
    padding-left: 1rem;
    padding-right: 0.75rem;
  }

  .lais-sidebar__logo,
  .lais-sidebar__compact-logo,
  .lais-sidebar__section-title,
  .lais-sidebar__link-label,
  .lais-sidebar__badge,
  .lais-sidebar__footer-text {
    transition-duration: 160ms;
    transition-property: opacity, max-width, transform;
    transition-timing-function: ease-out;
  }

  .lais-sidebar__logo,
  .lais-sidebar__section-title,
  .lais-sidebar__link-label,
  .lais-sidebar__badge,
  .lais-sidebar__footer-text {
    max-width: 0;
    opacity: 0;
    overflow: hidden;
    padding-left: 0;
    padding-right: 0;
    pointer-events: none;
    white-space: nowrap;
  }

  .lais-sidebar__section-title {
    display: block;
  }

  .lais-sidebar__logo,
  .lais-sidebar__link-label,
  .lais-sidebar__footer-text {
    transform: translateX(-6px);
  }

  .lais-sidebar__link-label,
  .lais-sidebar__footer-text {
    flex: 0 0 auto;
    min-width: 0;
  }

  .lais-sidebar__badge {
    border-width: 0;
    transform: scale(0.88);
  }

  .lais-sidebar__compact-logo {
    display: flex;
    width: 36px;
    height: 36px;
    align-items: center;
    justify-content: center;
    border: 1px solid rgb(226, 232, 240);
    border-radius: 9999px;
    background: rgb(245, 243, 255);
    color: rgb(46, 31, 77);
    font-size: 0.875rem;
    font-weight: 700;
    box-shadow: inset 0 0 0 1px rgba(46, 31, 77, 0.04);
  }

  .lais-sidebar--hover-collapse:hover .lais-sidebar__logo,
  .lais-sidebar--hover-collapse:hover .lais-sidebar__section-title,
  .lais-sidebar--hover-collapse:hover .lais-sidebar__link-label,
  .lais-sidebar--hover-collapse:hover .lais-sidebar__badge,
  .lais-sidebar--hover-collapse:hover .lais-sidebar__footer-text {
    max-width: 168px;
    opacity: 1;
    pointer-events: auto;
    transform: translateX(0);
  }

  .lais-sidebar--hover-collapse:hover .lais-sidebar__badge {
    border-width: 1px;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  .lais-sidebar--hover-collapse:hover .lais-sidebar__link-label,
  .lais-sidebar--hover-collapse:hover .lais-sidebar__footer-text {
    flex: 1 1 auto;
  }

  .lais-sidebar--hover-collapse:hover .lais-sidebar__badge {
    flex: 0 0 auto;
    transform: scale(1);
  }

  .lais-sidebar--hover-collapse:hover .lais-sidebar__compact-logo {
    max-width: 0;
    opacity: 0;
    transform: translateX(-6px);
  }
}
</style>
